import { SofaFormControl } from '@sofology/sofologycomponents';

import { ComponentTypeDescription, IfcAddressDescription, PropertyDescriptionTypes } from 'models';
import { PropertyValueType, SHOULD_BE_RENDERED_FUNC_NAME, SofaFormType } from './formHelperAdditionalData';
import { getComponentByInputType, getFieldSetComponent, getYearsMonthsComponent, getAddressComponent, getSortCodeComponent } from './getComponentHelper';

class ComponentGeneratorHelper {
    // eslint-disable-next-line sonarjs/cognitive-complexity
    public getComponents(form: SofaFormType | null, properties: Record<string, PropertyDescriptionTypes>, globalState: any): JSX.Element[] {
        if (!form || !properties) {
            return [];
        }

        let components: JSX.Element[] = [];

        for (let propertyKey in properties) {
            const property = properties[propertyKey];

            switch (true) {
                case property.componentType === ComponentTypeDescription.SortCode:
                    this.handleSortCodeComponent(property, form, components);
                    break;
                case property.componentType === ComponentTypeDescription.Address:
                    this.handleAddressComponent(property, form, components, globalState);
                    break;
                case property.componentType === ComponentTypeDescription.YearsMonths:
                    this.handleMonthsYearsComponent(property, propertyKey, form, components);
                    break;
                case property.properties && property.componentType !== ComponentTypeDescription.Address:
                    this.handleNonAddressComponent(property, propertyKey, form, components);
                    break;
                default:
                    this.handleSingleComponent(property, form, components);
                    break;
            }
        }

        return components;
    }

    private handleMonthsYearsComponent(property: PropertyDescriptionTypes, propertyKey: string, form: SofaFormType, components: JSX.Element[]): void {
        let monthsYearsControls: SofaFormControl<PropertyValueType>[] = [];
        let shouldBeRendered = true;

        for (let subPropKey in property.properties) {
            let subProp = property.properties[subPropKey];

            let control: SofaFormControl<PropertyValueType> = form.controls[subProp.name];

            shouldBeRendered = control[SHOULD_BE_RENDERED_FUNC_NAME]();

            if (shouldBeRendered) {
                monthsYearsControls.push(control);
            }
        }

        const isCorrectlyInitialized = monthsYearsControls.length === 2;
        if (isCorrectlyInitialized) {
            components.push(getYearsMonthsComponent(property.label, propertyKey, monthsYearsControls));
        }
    }

    private handleNonAddressComponent(property: PropertyDescriptionTypes, propertyKey: string, form: SofaFormType, components: JSX.Element[]): void {
        let subComponents: JSX.Element[] = [];
        let shouldBeRendered = true;

        for (let subPropKey in property.properties) {
            let subProp = property.properties[subPropKey];

            let control: SofaFormControl<PropertyValueType> = form.controls[subProp.name];

            shouldBeRendered = control[SHOULD_BE_RENDERED_FUNC_NAME]();

            if (shouldBeRendered) {
                const component = getComponentByInputType(control, subProp);

                subComponents.push(component);
            }
        }

        let wrapper = this.getWrapperComponent(property, propertyKey, subComponents);
        if (wrapper) {
            components.push(wrapper);
        }
    }

    private getWrapperComponent(property: PropertyDescriptionTypes, propertyKey: string, subComponents: JSX.Element[]): JSX.Element | null {
        if (subComponents.length === 0) {
            return null;
        }

        let componentKey = '';

        switch (property.componentType) {
            case ComponentTypeDescription.Fieldset:
                componentKey = `form-${propertyKey}-field-set`;

                return getFieldSetComponent(property.label, componentKey, subComponents);
            default:
                return null;
        }
    }

    private handleAddressComponent(property: PropertyDescriptionTypes, form: SofaFormType, components: JSX.Element[], globalState: any): void {
        const addressControl = form.controls[property.name] as SofaFormControl<string>;
        const addressComponent = getAddressComponent(property.label, addressControl, property as unknown as IfcAddressDescription, globalState, property.readonly);

        if (addressControl[SHOULD_BE_RENDERED_FUNC_NAME]()) {
            components.push(addressComponent);
        }
    }

    private handleSingleComponent(property: PropertyDescriptionTypes, form: SofaFormType, components: JSX.Element[]): void {
        const control: SofaFormControl<PropertyValueType> = form.controls[property.name];

        if (control[SHOULD_BE_RENDERED_FUNC_NAME]()) {
            const component = getComponentByInputType(control, property);

            components.push(component);
        }
    }

    private handleSortCodeComponent(sortCodeProperty: PropertyDescriptionTypes, form: SofaFormType, components: JSX.Element[]): void {
        const sortCodeControl: SofaFormControl<PropertyValueType> = form.controls[sortCodeProperty.name];

        if (sortCodeControl[SHOULD_BE_RENDERED_FUNC_NAME]()) {
            const component = getSortCodeComponent(sortCodeProperty, sortCodeControl);

            components.push(component);
        }
    }
}

export const componentGeneratorHelper = new ComponentGeneratorHelper();